/**
 * Search - Main search and log viewing page
 */
import { ReactElement, useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { Splitter, Flex, Spin } from 'antd'
import { fetchLogEventsCount } from 'actions/search/chart'
import {
  getChartData,
  getChartInterval,
  isFetchingChartData,
} from 'selectors/search/chart'
import {
  getCustomDateRange,
  getAttributeValueFilters,
  getTimeRange,
} from 'selectors/search/filters'
import { getManualSearchAt } from 'selectors/search/ui'
import { fetchLogs, fetchMoreLogs } from 'actions/search/logs'
import {
  getLogs,
  isFetchingLogs,
  isFetchingMoreLogs,
  hasMoreLogs,
} from 'selectors/search/logs'
import { TimeRange } from 'types/search/filters'
import type { LogEventCount, TimeInterval } from 'types/search/chart'
import type { Log } from 'types/search/logs'
import SearchBar from './SearchBar'
import AppliedFilters from './AppliedFilters'
import AttributesList from './AttributesList'
import AttributeValuesList from './AttributeValuesList'
import LogsChart from './LogsChart'
import LogsTable from './LogsTable'
import GuidedTour from './GuidedTour'
import styles from './Search.module.scss'

export interface SearchProps {
  className?: string
  chartData: LogEventCount[]
  chartInterval: TimeInterval | null
  fetchingChart: boolean
  appliedFilters: Array<{ attributeCode: string; value: string }>
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
  manualSearchAt: string | null
  logs: Log[]
  fetchingLogs: boolean
  fetchingMoreLogs: boolean
  hasMore: boolean
}

interface ReduxState {
  chartData: LogEventCount[]
  chartInterval: TimeInterval | null
  fetchingChart: boolean
  appliedFilters: Array<{ attributeCode: string; value: string }>
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
  manualSearchAt: string | null
  logs: Log[]
  fetchingLogs: boolean
  fetchingMoreLogs: boolean
  hasMore: boolean
}

const select = (): ReduxState => ({
  chartData: getChartData(),
  chartInterval: getChartInterval(),
  fetchingChart: isFetchingChartData(),
  appliedFilters: getAttributeValueFilters(),
  timeRange: getTimeRange(),
  customDateRange: getCustomDateRange(),
  manualSearchAt: getManualSearchAt(),
  logs: getLogs(),
  fetchingLogs: isFetchingLogs(),
  fetchingMoreLogs: isFetchingMoreLogs(),
  hasMore: hasMoreLogs(),
})

/**
 * Search - Main search page with layout and orchestration
 */
const Search = (props: SearchProps): ReactElement => {
  const {
    chartData,
    chartInterval,
    fetchingChart,
    appliedFilters,
    timeRange,
    customDateRange,
    manualSearchAt,
    logs,
    fetchingLogs,
    fetchingMoreLogs,
    hasMore,
  } = props

  const [chartRevision, setChartRevision] = useState<number>(0)

  // Auto-fetch chart data when filters change (attribute values, time range, or custom dates)
  useEffect(() => {
    fetchLogEventsCount()
  }, [appliedFilters, timeRange, customDateRange])

  // Fetch chart and logs when search is triggered
  useEffect(() => {
    if (!manualSearchAt) return

    fetchLogEventsCount()
    fetchLogs()
  }, [manualSearchAt])

  // Fetch logs when filters change (attribute values, time range, or custom dates)
  useEffect(() => {
    fetchLogs()
  }, [appliedFilters, timeRange, customDateRange])

  const handleChartPanelResize = useCallback(() => {
    // Increment revision to force Plotly to resize
    setChartRevision((prev) => prev + 1)
  }, [])

  const handleLoadMore = useCallback(() => {
    fetchMoreLogs()
  }, [])

  return (
    <div className={styles.container}>
      {/* Search Bar */}
      <SearchBar />

      {/* Applied Filters */}
      <AppliedFilters />

      {/* Main Content */}
      <Splitter style={{ height: 'calc(100% - 89px)' }}>
        {/* Attributes Panel */}
        <Splitter.Panel defaultSize="15%" min="10%" max="30%" collapsible>
          <div data-tour="attributes-panel" style={{ height: '100%' }}>
            <AttributesList />
          </div>
        </Splitter.Panel>

        {/* Values Panel */}
        <Splitter.Panel defaultSize="15%" min="10%" max="30%" collapsible>
          <div data-tour="values-panel" style={{ height: '100%' }}>
            <AttributeValuesList />
          </div>
        </Splitter.Panel>

        {/* Main Panel - Chart and Logs */}
        <Splitter.Panel>
          <Splitter layout="vertical" onResize={handleChartPanelResize}>
            {/* Chart Panel */}
            <Splitter.Panel defaultSize="30%" min="20%" max="50%">
              <div data-tour="logs-chart" style={{ height: '100%' }}>
                {fetchingChart ? (
                  <Flex
                    justify="center"
                    align="center"
                    style={{ height: '100%' }}
                  >
                    <Spin tip="Loading chart data..." />
                  </Flex>
                ) : (
                  <LogsChart
                    data={
                      chartData
                        ? chartData.map((item) => ({
                            date: item.date,
                            value: item.count,
                          }))
                        : []
                    }
                    revision={chartRevision}
                    interval={chartInterval}
                    timeRange={timeRange}
                    customDateRange={customDateRange}
                    isFetching={fetchingChart}
                  />
                )}
              </div>
            </Splitter.Panel>

            {/* Logs Table Panel */}
            <Splitter.Panel>
              <div data-tour="logs-table" style={{ height: '100%' }}>
                {fetchingLogs ? (
                  <Flex
                    justify="center"
                    align="center"
                    style={{ height: '100%' }}
                  >
                    <Spin tip="Loading logs..." />
                  </Flex>
                ) : (
                  <LogsTable
                    logs={logs}
                    hasMore={hasMore}
                    isFetchingMore={fetchingMoreLogs}
                    onLoadMore={handleLoadMore}
                  />
                )}
              </div>
            </Splitter.Panel>
          </Splitter>
        </Splitter.Panel>
      </Splitter>

      <GuidedTour />
    </div>
  )
}

export default connect(select)(Search)
